/**
 * Specialized API-to-Client Adapters
 *
 * Handles specific DTO types like EventTimeSlotDto, LeaveOfAbsenceDto, etc.
 */

import moment from "moment";
import {
  EventTimeSlotDto,
  ContinuousEventTimeSlotDto,
  LeaveOfAbsenceDto,
  SimpleWorkingHoursDto,
  WorkingHoursDto,
  WorkingHoursDtoSourceEnum,
} from "../../../../../api-client";
import { mobiliTheme } from "../../../../../themes/mobiliTheme";
import { CalendarEvent, ClientContinuousLunchBreak, ClientLunchBreak, ClientTimeSlot, ClientUser } from "../types";
import { adaptUserFromApi } from "./user.adapter";

/**
 * Adapt public holiday from WorkingHoursDto to ClientTimeSlot
 * Public holidays are returned as WorkingHoursDto items with source='public-holiday'
 */
export const adaptPublicHolidayFromApi = (publicHoliday: WorkingHoursDto): ClientTimeSlot => {
  const holidayBaseDate = publicHoliday.date || publicHoliday.start;

  return {
    id: publicHoliday.slotId || `public-holiday-${publicHoliday.userId}-${publicHoliday.start}`,
    start: moment(holidayBaseDate).startOf("day").toDate(),
    end: moment(holidayBaseDate).endOf("day").toDate(),
    type: "PublicHoliday",
    user: { id: publicHoliday.userId } as any,
    title: publicHoliday.holidayTitle || "Feiertag",
    isCancelled: false,
    hasNotes: false,
    isContinuous: false,
  };
};

/**
 * Adapt EventTimeSlotDto to ClientTimeSlot
 */
export const adaptEventTimeSlotFromApi = (eventTimeSlot: EventTimeSlotDto): ClientTimeSlot => {
  return {
    id: eventTimeSlot.id!,
    start: new Date(eventTimeSlot.start!),
    end: new Date(eventTimeSlot.end!),
    type: "EventTimeSlot",
    user: eventTimeSlot.user ? adaptUserFromApi(eventTimeSlot.user) : undefined,
    title: eventTimeSlot.title,
    isCancelled: false,
    hasNotes: false,
    isContinuous: false,
  };
};

/**
 * Adapt ContinuousEventTimeSlotDto to ClientTimeSlot
 */
export const adaptContinuousEventTimeSlotFromApi = (
  eventTimeSlot: ContinuousEventTimeSlotDto
): ClientTimeSlot => {
  return {
    id: eventTimeSlot.id!,
    start: new Date(eventTimeSlot.start!),
    end: new Date(eventTimeSlot.end!),
    type: "ContinuousEventTimeSlot",
    user: eventTimeSlot.user ? adaptUserFromApi(eventTimeSlot.user) : undefined,
    title: eventTimeSlot.title,
    isCancelled: false,
    hasNotes: false,
    isContinuous: true,
  };
};

/**
 * Adapt LeaveOfAbsenceDto to ClientTimeSlot
 */
export const adaptLeaveOfAbsenceFromApi = (loa: LeaveOfAbsenceDto): ClientTimeSlot => {
  return {
    id: loa.id!,
    start: new Date(loa.start!),
    end: new Date(loa.end!),
    type: "LeaveOfAbsence",
    user: loa.user ? adaptUserFromApi(loa.user) : undefined,
    title: `${loa.loAType || ""}`,
    loaType: loa.loAType,
    isCancelled: false,
    hasNotes: false,
    isContinuous: false,
  };
};

export const adaptDerivedLunchBreak = (lunchBreak: WorkingHoursDto): ClientLunchBreak => {
  // Generate a stable ID for lunch breaks: use slotId if available, otherwise create one from userId + start time
  const id = lunchBreak.slotId || `lunchbreak-${lunchBreak.userId}-${lunchBreak.start}`;
  
  return {
    id,
    slotId: lunchBreak.slotId, // Preserve original slotId (may be undefined)
    start: new Date(lunchBreak.start),
    end: new Date(lunchBreak.end),
    userId: lunchBreak.userId,
    isException: lunchBreak.source === WorkingHoursDtoSourceEnum.Exception,
    exceptionId: lunchBreak.exceptionId,
  };
};

export const clientLunchBreakToCalendarEvent = (lunchBreak: ClientLunchBreak): CalendarEvent => {
  const resourceId = lunchBreak.userId;

  return {
    id: lunchBreak.id,
    title: "Pause",
    start: lunchBreak.start.toISOString(),
    end: lunchBreak.end.toISOString(),
    durationEditable: true,
    // Ensure resourceId is a string (FullCalendar requirement for consistent diffing)
    resourceId,
    resizable: true,
    classNames: ["lunchbreak"],

    // TODO we could have a different color for exceptions
    color: lunchBreak.isException ? mobiliTheme.palette.info.light : mobiliTheme.palette.info.light,

    // All custom properties go in extendedProps
    extendedProps: {
      eventType: 'working-hours',
      timeSlotId: lunchBreak.id,
      timeSlotType: 'LunchBreak',
      slotId: lunchBreak.slotId, // Original API slotId (may be undefined)

      user: {id : lunchBreak.userId} as ClientUser,
      isContinuous: false,
      isException: lunchBreak.isException,
      exceptionId: lunchBreak.exceptionId,
    },
  };
};

export const adaptDerivedLunchBreakForContinuous = (
  lunchBreak: WorkingHoursDto
): ClientContinuousLunchBreak=> {
  const id = lunchBreak.slotId || `lunchbreak-${lunchBreak.userId}-${lunchBreak.start}`;

  const start = new Date(lunchBreak.start);
  const end = new Date(lunchBreak.end);

  // FullCalendar requires daysOfWeek as numbers 0 (Sunday) - 6 (Saturday)
  // Work plan dayOfWeek may be 0=Sunday, 1=Monday...
  const day = start.getDay(); // fallback if dayOfWeek not in API

  return {
    id,
    weeklyWorkDay: lunchBreak.weeklyWorkDay,
    slotId: lunchBreak.slotId,
    userId: lunchBreak.userId,
    start: new Date(lunchBreak.start),
    end: new Date(lunchBreak.end),
    startTime: moment(start).format("HH:mm:ss"),
    endTime: moment(end).format("HH:mm:ss"),
    daysOfWeek: [day]
  };
};

/**
 * Convert adapted continuous lunch break to FullCalendar event
 */
export const clientContinuousLunchBreakToCalendarEvent = (
  lunchBreak: ReturnType<typeof adaptDerivedLunchBreakForContinuous>
): CalendarEvent => {
   const resourceId = lunchBreak.userId;
  return {
    id: lunchBreak.id,
    title: "Pause",
    weeklyWorkDay: lunchBreak.weeklyWorkDay,
    start: lunchBreak.start,
    end: lunchBreak.end,
    // Ensure resourceId is a string (FullCalendar requirement for consistent diffing)

    startTime: lunchBreak.startTime,
    endTime: lunchBreak.endTime,
    daysOfWeek: lunchBreak.daysOfWeek,
    resourceId: lunchBreak.userId,
    durationEditable: true, // usually can't drag recurring lunch break
    resizable: true,
    classNames: ["lunchbreak"],
    color:  mobiliTheme.palette.info.light,
    extendedProps: {
      eventType: "working-hours",
      timeSlotType: "LunchBreak",
      slotId: lunchBreak.slotId,
      user: { id: lunchBreak.userId } as ClientUser,
      isContinuous: true,
    },
  };
};

/**
 * Convert public holiday ClientTimeSlot to FullCalendar event
 * Public holidays are displayed similarly to leave of absence events
 */
export const clientPublicHolidayToCalendarEvent = (publicHoliday: ClientTimeSlot): CalendarEvent => {
  const resourceId = publicHoliday.user?.id || "";

  return {
    id: publicHoliday.id,
    title: publicHoliday.title || "Feiertag",
    start: publicHoliday.start.toISOString(),
    end: publicHoliday.end.toISOString(),
    editable: false,
    startEditable: false,
    durationEditable: false,
    resourceEditable: false,
    resourceId,
    resizable: false,
    classNames: ["public-holiday"],
    color: mobiliTheme.palette.warning.main,

    // All custom properties go in extendedProps
    extendedProps: {
      eventType: "working-hours",
      timeSlotType: "PublicHoliday",
      source: "public-holiday",
      user: publicHoliday.user,
      isContinuous: false,
    },
  };
};
